<script setup>
import {
    FormCheckbox,
    FormCheckboxGroup,
    FormCheckboxGroupOption,
    FormGroupListbox,
    FormInput,
    FormListbox,
    FormRadioGroup,
    FormRadioGroupOption,
    FormSwitch,
    FormTextarea,
    FormTags,
    FormDropdown,
    FormDropdownItem,
    FormDropdownTrigger,
} from "@Components/Form";
import { PrimaryButton, SecondaryButton } from "@Components/Button";
import { setTheme } from "@/theme";
import { HeartIcon, RocketIcon, StarIcon, UserIcon } from "lucide-vue-next";
import { ref, reactive } from "vue";

const zList = [
    {
        z: 0,
        bg: "dark:bg-gray-950 bg-white",
    },
    {
        z: 1,
        bg: "dark:bg-gray-900 bg-gray-50",
    },
    {
        z: 2,
        bg: "dark:bg-gray-800 bg-gray-100",
    },
    {
        z: 3,
        bg: "dark:bg-gray-700 bg-gray-200",
    },
];

const values = reactive({
    switch: true,
    listbox: 1,
    groupListbox: "group_1_item_1",
    checkboxGroup: {
        option1: true,
        option2: false,
        option3: false,
        option4: false,
    },
    checkbox: true,
    radioGroup: "option1",
    tags: ["tag1", "tag2", "tag3"],
});
</script>
<template>
    <div class="w-screen h-screen">
        <div class="px-8 py-8 bg-white border-b border-gray-200 dark:border-white/10 dark:bg-gray-900">
            <div class="flex gap-2">
                <PrimaryButton @click="setTheme('dark')">Dark</PrimaryButton>
                <PrimaryButton @click="setTheme('light')">Light</PrimaryButton>
                <PrimaryButton @click="setTheme(undefined)">Auto</PrimaryButton>
            </div>
        </div>
        <div v-for="z in zList" :key="z.z" :class="['px-8 py-4', z.bg]">
            <h2 class="text-lg font-medium text-gray-900 dark:text-white">Z-{{ z.z }}</h2>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Input Elements</h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-3">
                        <FormInput label="Form Input" :z="z.z" placeholder="Placeholder" hint="Hint" help="Help" />
                    </div>
                    <div class="col-span-3">
                        <FormInput label="Form Input" :z="z.z" placeholder="Placeholder" hint="Hint" help="Help" sensitive />
                    </div>
                    <div class="col-span-3">
                        <FormInput label="Form Input" :z="z.z" placeholder="Placeholder" :errors="['Error message']" />
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Textarea Elements</h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-3">
                        <FormTextarea label="Form Textarea" rows="5" :z="z.z" placeholder="Placeholder" hint="Hint" help="Help" />
                    </div>
                    <div class="col-span-3">
                        <FormTextarea label="Form Textarea" rows="5" :z="z.z" placeholder="Placeholder" :errors="['Error message']" />
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Switch Elements</h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-3">
                        <FormSwitch label="Form Switch" :z="z.z" v-model="values.switch" />
                    </div>
                    <div class="col-span-3">
                        <FormSwitch label="Form Switch" :z="z.z" size="small" v-model="values.switch" />
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Switch Elements</h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-3">
                        <FormCheckbox :z="z.z" v-model="values.checkbox" />
                    </div>
                    <div class="col-span-3">
                        <FormCheckbox :z="z.z" disabled v-model="values.checkbox" />
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Listbox Elements</h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-3">
                        <FormListbox
                            label="Form Listbox"
                            :z="z.z"
                            hint="Hint"
                            help="Help"
                            v-model="values.listbox"
                            :items="[
                                { id: 1, name: 'Item 1', icon: HeartIcon },
                                { id: 2, name: 'Item 2', icon: StarIcon },
                                { id: 3, name: 'Item 3', icon: RocketIcon },
                            ]" />
                    </div>
                    <div class="col-span-3">
                        <FormListbox
                            label="Form Listbox"
                            :z="z.z"
                            v-model="values.listbox"
                            :items="[
                                { id: 1, name: 'Item 1', icon: HeartIcon },
                                { id: 2, name: 'Item 2', icon: StarIcon },
                                { id: 3, name: 'Item 3', icon: RocketIcon },
                            ]"
                            :errors="['Error message']" />
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Group Listbox Elements</h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-3">
                        <FormGroupListbox
                            label="Form Group Listbox"
                            :z="z.z"
                            hint="Hint"
                            help="Help"
                            v-model="values.groupListbox"
                            :groups="[
                                {
                                    name: 'Groupe 1',
                                    items: [
                                        { id: 'group_1_item_1', name: 'Item 1', icon: HeartIcon, price: '10/image' },
                                        { id: 'group_1_item_2', name: 'Item 2', icon: StarIcon },
                                        { id: 'group_1_item_3', name: 'Item 3', icon: RocketIcon },
                                    ],
                                },
                                {
                                    name: 'Groupe 2',
                                    items: [
                                        { id: 'group_2_item_1', name: 'Item 1', icon: HeartIcon },
                                        { id: 'group_2_item_2', name: 'Item 2', icon: StarIcon },
                                        { id: 'group_2_item_3', name: 'Item 3', icon: RocketIcon },
                                    ],
                                },
                            ]" />
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Dropdown Elements</h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-3">
                        <FormDropdown :z="z.z" position="right">
                            <template #trigger>
                                <FormDropdownTrigger :as="PrimaryButton" :z="z.z">Dropdown</FormDropdownTrigger>
                            </template>
                            <FormDropdownItem label="Item 1" :icon="HeartIcon" :z="z.z" />
                            <FormDropdownItem label="Item 2" :icon="StarIcon" :z="z.z" />
                            <FormDropdownItem label="Item 3" :icon="RocketIcon" :z="z.z" />
                        </FormDropdown>
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Button Elements</h3>
                <div class="grid grid-cols-4 gap-6">
                    <div>
                        <PrimaryButton :z="z.z">Primary</PrimaryButton>
                    </div>
                    <div>
                        <PrimaryButton :z="z.z" disabled>Primary</PrimaryButton>
                    </div>
                    <div>
                        <PrimaryButton :z="z.z" :icon="HeartIcon">Primary</PrimaryButton>
                    </div>
                    <div>
                        <PrimaryButton :z="z.z" processing>Primary</PrimaryButton>
                    </div>
                    <div>
                        <SecondaryButton :z="z.z">Secondary</SecondaryButton>
                    </div>
                    <div>
                        <SecondaryButton :z="z.z" disabled>Secondary</SecondaryButton>
                    </div>
                    <div>
                        <SecondaryButton :z="z.z" :icon="HeartIcon">Secondary</SecondaryButton>
                    </div>
                    <div>
                        <SecondaryButton :z="z.z" processing>Secondary</SecondaryButton>
                    </div>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Form Checkbox Group</h3>
                <div>
                    <FormCheckboxGroup label="Form Checkbox Group" :z="z.z">
                        <div class="grid grid-cols-1 gap-6 lg:grid-cols-4 sm:grid-cols-2">
                            <FormCheckboxGroupOption
                                :z="z.z"
                                v-model="values.checkboxGroup.option1"
                                title="Option 1"
                                description="Description Option 1"
                                :icon="HeartIcon" />
                            <FormCheckboxGroupOption
                                :z="z.z"
                                v-model="values.checkboxGroup.option2"
                                title="Option 2"
                                description="Description Option 2"
                                :icon="StarIcon" />
                            <FormCheckboxGroupOption
                                :z="z.z"
                                v-model="values.checkboxGroup.option3"
                                title="Option 3"
                                description="Description Option 3"
                                :icon="RocketIcon" />
                            <FormCheckboxGroupOption
                                :z="z.z"
                                v-model="values.checkboxGroup.option4"
                                title="Option 4"
                                description="Description Option 4"
                                disabled
                                :icon="UserIcon" />
                        </div>
                    </FormCheckboxGroup>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Form Radio Group</h3>
                <div>
                    <FormRadioGroup label="Form Radio Group" :z="z.z" v-model="values.radioGroup">
                        <div class="grid grid-cols-1 gap-6 lg:grid-cols-4 sm:grid-cols-2">
                            <FormRadioGroupOption :z="z.z" value="option1" title="Option 1" description="Description Option 1" :icon="HeartIcon" />
                            <FormRadioGroupOption :z="z.z" value="option2" title="Option 2" description="Description Option 2" :icon="StarIcon" />
                            <FormRadioGroupOption :z="z.z" value="option3" title="Option 3" description="Description Option 3" :icon="RocketIcon" />
                            <FormRadioGroupOption :z="z.z" value="option4" title="Option 4" description="Description Option 4" disabled :icon="UserIcon" />
                        </div>
                    </FormRadioGroup>
                </div>
            </div>

            <div class="mt-6 space-y-6">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Form Tags</h3>
                <div>
                    <FormTags label="Form Tags" :z="z.z" v-model="values.tags" />
                </div>
            </div>
        </div>
    </div>
</template>
